import { FormEvent, useState, useEffect } from "react";
import { PSCView } from "./PSCView";
import { useNavigate } from "react-router-dom";
import {
  ENDOMETRIAL_BIOPSY,
  INTRA_UTERINE_DEVICE_INSERTION,
  ROUTINE_PESSARY_CARE,
  VULVA_PUNCH_BIOPSY,
} from "navigation/Paths";
import {
  RatingInstrumentSubmissionContext,
  RatingInstrumentSubmissionItem,
  RatingInstrumentSubmissionModule,
} from "services/database/rating_instruments/schemas/RatingInstrumentSubmission";

export const PSCContainer = ({
  module,
  context,
}: {
  module: RatingInstrumentSubmissionModule;
  context: RatingInstrumentSubmissionContext;
}) => {
  const [scores, setScores] = useState<{ [id: string]: number }>({});
  const [comments, setComments] = useState<{ [id: string]: string }>({});
  const [page, setPage] = useState(0);
  const [unselectedItems, setUnselectedItems] = useState(getUnselectedItems());
  const [traineeId, setTraineeId] = useState<string | null>(null);

  const navigate = useNavigate();
  console.log(`grs: page: ${page}`);

  useEffect(() => {
    function filterUnselectedItems() {
      let data = getItems();
      var missedItems = [];
      for (const item of data.items) {
        if (isNaN(item.score)) {
          missedItems.push({ id: item.id });
        }
      }
      return missedItems;
    }

    function getItems() {
      let items = getModuleData(module).data.reduce(
        (arr: { id: string; score: number }[], item) => {
          if (item.id !== null) {
            arr.push({
              id: item.id,
              score: scores[item.id],
            });
          }
          return arr;
        },
        []
      );

      return {
        items: items,
      };
    }

    setUnselectedItems(filterUnselectedItems());
  }, [scores, module]);

  function getUnselectedItems() {
    let data = getDataToSubmit();
    var missedItems = [];
    for (const item of data.items) {
      if (isNaN(item.score)) {
        missedItems.push({ id: item.id });
      }
    }
    return missedItems;
  }

  function setComment(id: string, text: string) {
    let newComments: { [id: string]: string } = {
      ...comments,
    };
    newComments[id] = text;
    setComments({
      ...newComments,
    });
  }

  function setScore(id: string, score: number) {
    let newScores = {
      ...scores,
    };
    newScores[id] = score;
    setScores({
      ...newScores,
    });
  }

  function getTotalScore() {
    let totalScore = 0;
    for (var score in scores) {
      totalScore += scores[score];
    }
    return totalScore;
  }

  function getMaxScore() {
    let maxScore = 0;
    getModuleData(module).data.map((row) => (maxScore += row.id ? 2 : 0));
    return maxScore;
  }

  function getSubmitData() {
    let submitData = getModuleData(module).data.reduce((arr: any[], row) => {
      if (row.id) {
        arr.push({
          id: null,
          item: row.item,
          name: row.name,
          score: scores[row.id],
          comment: comments[row.id] ?? "",
        });
      }
      return arr;
    }, []);

    return submitData;
  }

  function getDataToSubmit() {
    let items = getModuleData(module).data.reduce(
      (arr: RatingInstrumentSubmissionItem[], item) => {
        if (item.id !== null) {
          arr.push({
            id: item.id,
            name: item.name,
            score: scores[item.id],
            comment: comments[item.id] ?? "",
          });
        }
        return arr;
      },
      []
    );

    return {
      items: items,
    };
  }

  function startNewTest() {
    setScores({});
    setComments({});
    setTraineeId(null);
    setPage(0);
  }

  async function submitResult() {
    setPage(2);
  }

  function goToSubmit(e: FormEvent) {
    let data = getDataToSubmit();
    // var isItemMissing = false;
    var missedItems = [];
    for (const item of data.items) {
      if (isNaN(item.score)) {
        e.preventDefault();
        missedItems.push({ id: item.id, name: item.name });
      }
    }

    missedItems.length !== 0
      ? alert("Please select a score for each item prior to proceeding.")
      : setPage(1);
  }

  const functions: { [k: string]: any } = {};
  functions.setScore = setScore;
  functions.setTraineeId = setTraineeId;
  functions.setComment = setComment;
  functions.goToSubmit = goToSubmit;
  functions.goToSuccess = () => submitResult();
  functions.goToRate = () => setPage(0);
  functions.goToNewRate = () => startNewTest();
  functions.backToPSCMenu = () => navigate("..");

  const objects: { [k: string]: any } = {};
  objects.rateData = getModuleData(module).data;
  objects.submitData = getSubmitData();
  objects.comments = comments;
  objects.scores = scores;
  objects.page = page;
  objects.currentModuleName = getModuleData(module).title;
  objects.traineeId = traineeId;
  objects.totalScore = getTotalScore();
  objects.maxScore = getMaxScore();
  objects.moduleId = module;
  objects.moduleNote = getModuleData(module).note;
  objects.unselectedItems = unselectedItems;

  return <PSCView functions={functions} objects={objects} />;
};

const endometrialBiopsyData = [
  {
    id: null,
    item: null,
    name: "Prepares for the procedure",
  },
  {
    id: "1",
    item: "1.",
    name: "Gloves",
  },
  {
    id: "2",
    item: "2.",
    name: "Lubricated or warmed speculum of appropriate size",
  },
  {
    id: "3",
    item: "3.",
    name: "Light source",
  },
  {
    id: "4",
    item: "4.",
    name: "10% formalin container labelled with patient's name",
  },
  {
    id: "5",
    item: "5.",
    name: "Ring forceps with 4x4 gauze soaked in cleansing solution/Iodine-soaked swabs",
  },
  {
    id: "6",
    item: "6.",
    name: "Tenaculum",
  },
  {
    id: "7",
    item: "7.",
    name: "Endometrial suction catheter/Pipelle",
  },
  {
    id: "8",
    item: "8.",
    name: "Gauze or swabs to stop the bleeding at the end of the procedure",
  },
  {
    id: "9",
    item: "9.",
    name: "Uterine sound/dilator or an Os finder for difficult cases",
  },
  {
    id: null,
    item: null,
    name: "Examines the patient",
  },
  {
    id: "10",
    item: "10.",
    name: "Performs a bimanual exam in an attempt to determine the position of uterus (anteverted/retroverted) and reports any gross abnormality",
  },
  {
    id: null,
    item: null,
    name: "Performs the procedure",
  },
  {
    id: "11",
    item: "11.",
    name: "Gently inserts the speculum centering the cervix between speculum blades",
  },
  {
    id: "12",
    item: "12.",
    name: "Reports normal cervix (no purulent discharge or sign of active cervicitis)",
  },
  {
    id: "13",
    item: "13.",
    name: "Cleanes the cervix using ring forceps with 4x4 gauze soaked in cleansing solution or Iodine-soaked swabs",
  },
  {
    id: "14",
    item: "14.",
    name: "Grasps the cervix correctly with the tenaculum (i.e., anterior lip for anteverted uterus and posterior lip for retroverted uterus)",
  },
  {
    id: "15",
    item: "15.",
    name: "Inserts the endometrial suction catheter/Pipelle through the endocervical canal while applying traction on the tenaculum if being used",
  },
  {
    id: "16",
    item: "16.",
    name: "Determines the length of uterine cavity using endometrial Pipelle/suction catheter before tissue sampling (typically 5-9 cm)",
  },
  {
    id: "17",
    item: "17.",
    name: "Withdrows internal piston fully to create suction",
  },
  {
    id: "18",
    item: "18.",
    name: "Maintains suction during the procedure by ensuring the tip of catheter does not exit the endometrial cavity",
  },
  {
    id: "19",
    item: "19.",
    name: "Uses a slow 360-degree rotation with an in-and-out movement of catheter simultaneously to collect the specimen",
  },
  {
    id: "20",
    item: "20.",
    name: "Extrudes the specimen into the formalin solution without contaminating the catheter tip",
  },
  {
    id: "21",
    item: "21.",
    name: "Ensures adequacy of the sample by examining the collected specimen",
  },
  {
    id: "22",
    item: "22.",
    name: "Considers numerous passes until satisfied with adequate sampling",
  },
  {
    id: "23",
    item: "23.",
    name: "Removes the tenaculum avoiding excessive trauma to the cervix",
  },
  {
    id: "24",
    item: "24.",
    name: "Gently removes speculum to maximize patient comfort",
  },
  {
    id: "25",
    item: "25.",
    name: "Maintains any instrument entering uterine cavity sterile throughout the procedure",
  },
];

const routinePessaryCareData = [
  {
    id: null,
    item: null,
    name: "Prepares for the procedure",
  },
  {
    id: "1",
    item: "1.",
    name: "Gloves",
  },
  {
    id: "2",
    item: "2.",
    name: "Speculum of appropriate size",
  },
  {
    id: "3",
    item: "3.",
    name: "Lubricant",
  },
  {
    id: "4",
    item: "4.",
    name: "Light source",
  },
  {
    id: "5",
    item: "5.",
    name: "Ring forceps with 4x4 gauze or proctoscopy swabs to move tissue and optimize visual field",
  },
  {
    id: null,
    item: null,
    name: "Examines the patient",
  },
  {
    id: "6",
    item: "6.",
    name: "Examines the vulva for atrophy, irritation, or lesions",
  },
  {
    id: "7",
    item: "7.",
    name: "Performs a bimanual exam ensuring there is no change in the fit of the pessary",
  },
  {
    id: null,
    item: null,
    name: "Performs the procedure",
  },
  {
    id: "8",
    item: "8.",
    name: "Hooks the index finger laterally between the pessary and the vaginal wall to break suction",
  },
  {
    id: "9",
    item: "9.",
    name: "Rotates the pessary towards the sagittal plane, and removes it gently",
  },
  {
    id: "10",
    item: "10.",
    name: "Washes the pessary with liquid soap and water as recommended by manufacturer's instruction manual",
  },
  {
    id: "11",
    item: "11.",
    name: "Examines the pessary for defects",
  },
  {
    id: "12",
    item: "12.",
    name: "Performs a speculum examination of the vagina to check for signs of erosions or infection",
  },
  {
    id: "13",
    item: "13.",
    name: "Applies a small amount of lubricant on the leading ledge of the pessary",
  },
  {
    id: "14",
    item: "14.",
    name: "Folds the pessary in half and inserts the pessary into the vagina using the proper direction and technique recommended by the manufacturer",
  },
  {
    id: "15",
    item: "15.",
    name: "Performs a vaginal exam to ensure correct placement of the pessary",
  },
];

const intrauterineInsertionData = [
  {
    id: null,
    item: null,
    name: "Prepares for the procedure",
  },
  {
    id: "1",
    item: "1.",
    name: "Gloves",
  },
  {
    id: "2",
    item: "2.",
    name: "Lubricated or warmed speculum of appropriate size",
  },
  {
    id: "3",
    item: "3.",
    name: "Light source",
  },
  {
    id: "4",
    item: "4.",
    name: "Ring forceps with 4x4 gauze soaked in cleansing solution/Iodine-soaked swabs",
  },
  {
    id: "5",
    item: "5.",
    name: "Tenaculum",
  },
  {
    id: "6",
    item: "6.",
    name: "Uterine sound/endometrial Pipelle to measure the uterine cavity depth",
  },
  {
    id: "7",
    item: "7.",
    name: "IUD/IUS (left unpacked until the uterine cavety depth is measured successfully)",
  },
  {
    id: "8",
    item: "8.",
    name: "Long scissors",
  },
  {
    id: "9",
    item: "9.",
    name: "An Os finder or dilator for difficult cases",
  },
  {
    id: "10",
    item: "10.",
    name: "Gauze or swabs to stop the bleeding at the end of the procedure",
  },
  {
    id: null,
    item: null,
    name: "Examines the patient",
  },
  {
    id: "11",
    item: "11.",
    name: "Performs a bimanual exam in an attempt to determine the position of uterus (anteverted/retroverted) and reports any gross abnormality",
  },
  {
    id: null,
    item: null,
    name: "Performs the procedure",
  },
  {
    id: "12",
    item: "12.",
    name: "Gently inserts the speculum centering the cervix between speculum blades",
  },
  {
    id: "13",
    item: "13.",
    name: "Reports normal cervix and obtains STI swabs if previously not obtained",
  },
  {
    id: "14",
    item: "14.",
    name: "Cleanes the cervix using ring forceps with 4x4 gauze soaked in cleansing solution or Iodine-soaked swabs",
  },
  {
    id: "15",
    item: "15.",
    name: "Grasps the cervix correctly with the tenaculum (i.e., at anterior lip for anteverted uterus and posterior lip for retroverted uterus",
  },
  {
    id: "16",
    item: "16.",
    name: "Inserts the uterine sound or Os finder through the endocervical canal while applying traction on the tenaculum if used",
  },
  {
    id: "17",
    item: "17.",
    name: "Determines the length of uterine cavity correctly (at least 6 cm required)",
  },
  {
    id: "18",
    item: "18.",
    name: "Loads the device into the insertion tube maintaining correct orientation (i.e., arms of device in horizontal position and parallel to the flange)",
  },
  {
    id: "19",
    item: "19.",
    name: "Sets the flange to the determined depth",
  },
  {
    id: "20",
    item: "20.",
    name: "Inserts the device through the endocervial canal while applying traction on the tenaculum (if being used) until the flange is at the distance from the cervix recommanded by the manufacturer",
  },
  {
    id: "21",
    item: "21.",
    name: "Releases the device arms",
  },
  {
    id: "22",
    item: "22.",
    name: "Advances the device until the flange comes into contact with the cervix indicating that the device is positioned in the fundal area of the uterine cavity",
  },
  {
    id: "23",
    item: "23.",
    name: "Releases the IUD as recommended by the manufacturer's instruction manual",
  },
  {
    id: "24",
    item: "24.",
    name: "Removes the inserter tube +/- the plunger without pulling the device out",
  },
  {
    id: "25",
    item: "25.",
    name: "Cuts threads to appropriate length (about 3 cm from the cervical os)",
  },
  {
    id: "26",
    item: "26.",
    name: "Removes the tenaculum avoiding excessive trauma to the cervix",
  },
  {
    id: "27",
    item: "27.",
    name: "Gently removes speculum to maximize patient comfort",
  },
  {
    id: "28",
    item: "28.",
    name: "Maintains any instruments intering uterine cavity sterile throughout the procedure",
  },
];

const vulvaPunchBiopsyData = [
  {
    id: null,
    item: null,
    name: "Prepares for the procedure",
  },
  {
    id: "1",
    item: "1.",
    name: "Gloves",
  },
  {
    id: "2",
    item: "2.",
    name: "Cleansing agent of choice (Alcohol, Iodine, Chlorhexidine) on a sterile gauze",
  },
  {
    id: "3",
    item: "3.",
    name: "2-5 ml syringe filled with local anesthetic (2% Licodaine/Xylocaine with or without Epinephrine) and a 20-30 gauge needle",
  },
  {
    id: "4",
    item: "4.",
    name: "10% formalin container labelled with patient's name",
  },
  {
    id: "5",
    item: "5.",
    name: "Desired puch biopsy cylindrical blade (Keyes-type) 3-5 mm instrument to avoid requiring suturing for wound closure",
  },
  {
    id: "6",
    item: "6.",
    name: "Tissue forceps or a 16-20 gauge, 1 1/4 inch needle for elevating the specimen",
  },
  {
    id: "7",
    item: "7.",
    name: "Iris scissors",
  },
  {
    id: "8",
    item: "8.",
    name: "Sterile gauze to stop the bleeding",
  },
  {
    id: "9",
    item: "9.",
    name: "Silver Nitrate sticks or 20% Aluminium Chlorohydrate (ACH) or Ferric Sulfate 20% (Monsel's paste) to stop the bleeding",
  },
  {
    id: null,
    item: null,
    name: "Performs the procedure",
  },
  {
    id: "10",
    item: "10.",
    name: "Cleanses the skin",
  },
  {
    id: "11",
    item: "11.",
    name: "Infiltrates the skin with local anesthetic",
  },
  {
    id: "12",
    item: "12.",
    name: "Ensures patient is well anesthetized",
  },
  {
    id: "13",
    item: "13.",
    name: "Maintains vulva tissue retracted to facilitate the punch biopsy rotation",
  },
  {
    id: "14",
    item: "14.",
    name: "Uses back and forth rotating movement instead of pressure on punch biopsy device",
  },
  {
    id: "15",
    item: "15.",
    name: "Grasps specimen using a tissue forceps or a needle to elevate the specimen",
  },
  {
    id: "16",
    item: "16.",
    name: "Ensures enough depth reached before excising the tissue",
  },
  {
    id: "17",
    item: "17.",
    name: "Excises the tissue fully using Iris scissors",
  },
  {
    id: "18",
    item: "18.",
    name: "Transfers specimen successfully to the formalin container",
  },
  {
    id: "19",
    item: "19.",
    name: "Achieves homeostasis using available agents for example Silver Nitrate sticks or 20% Aluminium Chlorohydrate (ACH) or Ferric Sulfate 20% (Monsel's paste)",
  },
  {
    id: "20",
    item: "20.",
    name: "Maintains any instruments intering uterine cavity sterile throughout the procedure",
  },
];

const modulesData = {
  endometrial_biopsy: {
    title: "Endometrial Biopsy module",
    data: endometrialBiopsyData,
    id: ENDOMETRIAL_BIOPSY,
    note: "", // she asked to remove the notes  "*Items 5, 9, 10 and 13 are lower priority items as during the national consultation their importance was undetermined",
  },
  routine_pessary_care: {
    title: "Routine Pessary Care",
    data: routinePessaryCareData,
    id: ROUTINE_PESSARY_CARE,
    note: "", // she asked to remove the notes   "*Items 4 and 6 are lower priority items as during the national consultation their importance was undetermined",
  },
  intrauterine_insertion: {
    title: "Intrauterine Device/System (IUD/IUS) Insertion",
    data: intrauterineInsertionData,
    id: INTRA_UTERINE_DEVICE_INSERTION,
    note: "", // she asked to remove the notes   "*Items 4, 9, 10 and 11 are lower priority items as during the national consultation their importance was undetermined",
  },
  vulva_punch_bipsy: {
    title: "Vulva Punch Biopsy",
    data: vulvaPunchBiopsyData,
    id: VULVA_PUNCH_BIOPSY,
    note: "", // she asked to remove the notes   "Item 19 is of lower priority as during the national consultation on their importance, it did not rank into> 70% of important or very important",
  },
  default: {
    title: "",
    data: [],
    id: "",
    note: "",
  },
};

const getModuleData = (moduleId: string) => {
  switch (moduleId) {
    case ENDOMETRIAL_BIOPSY:
      return modulesData.endometrial_biopsy;
    case ROUTINE_PESSARY_CARE:
      return modulesData.routine_pessary_care;
    case INTRA_UTERINE_DEVICE_INSERTION:
      return modulesData.intrauterine_insertion;
    case VULVA_PUNCH_BIOPSY:
      return modulesData.vulva_punch_bipsy;
    default:
      return modulesData.default;
  }
};
